import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, Button, Spinner, Text, VStack } from '@chakra-ui/react';
import { getCurrentEnvironment, logger } from '@kuow/core';
import { useFormContext } from './context/FormContext';
import { loadPaypalSdk, type PaypalButton, type PaypalButtonOptions } from './paypalSdk';
import { createPaypalCheckout, loadPaypalApprovalDonor, paypalTerms, prefillPaypalDonor, type PaypalApproval } from './paypalCheckout';
import { DonationTotalBar } from './DonationTotalBar';
import { submitBillingUpdateFromState } from './services/donationSubmission';
import { getPrimarySubmissionError, SUBMISSION_ERROR_FALLBACK_MESSAGE } from './utils/submissionErrors';
import { paypalErrorDiagnostic } from './paypalDiagnostics';

interface Props {
  clientId: string;
}

export function PayPalButtons({ clientId }: Props) {
  const { state, dispatch } = useFormContext();
  const latest = useRef(state);
  latest.current = state;
  const container = useRef<HTMLDivElement>(null);
  const busy = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [buttonsHeight, setButtonsHeight] = useState(0);
  const [attempt, setAttempt] = useState(0);
  const kind = state.formData.frequency === 'monthly' ? 'subscription' : 'order';
  const terms = state.formDetails ? paypalTerms(state.formData, state.formDetails) : '';

  useLayoutEffect(() => {
    const element = container.current;
    if (!element || loading) return;
    const measure = () => setButtonsHeight(element.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [loading]);

  useEffect(() => {
    const element = container.current;
    if (!element) return;
    let disposed = false;
    let failed = false;
    let ready = false;
    let approvalStage = 'approval-callback';
    let renderTimer: number | undefined;
    // The SDK can show a prerendered button before its render promise settles.
    element.inert = true;
    let approvalHandled = false;
    let approval: PaypalApproval | null = null;
    let fundingSource = 'paypal';
    const buttons: PaypalButton[] = [];
    setError(null);
    setLoading(true);
    const release = () => {
      busy.current = false;
      if (!disposed) dispatch({ type: 'SET_SUBMITTING', payload: false });
    };
    const closeButtons = () => {
      for (const button of buttons) void button.close().catch(() => undefined);
      element.replaceChildren();
    };
    const fail = (stage: string) => {
      if (disposed || failed) return;
      failed = true;
      ready = false;
      window.clearTimeout(renderTimer);
      element.inert = true;
      closeButtons();
      logger.warn('PayPal checkout failed', { stage, kind });
      const diagnostic = getCurrentEnvironment() === 'production' ? '' : ` (Diagnostic: ${stage})`;
      setError(`PayPal could not complete approval. Please try again or choose another payment method.${diagnostic}`);
      setLoading(false);
      release();
    };
    const create = async () => {
      const current = latest.current;
      if (disposed || failed || !ready || !current.formDetails || current.formData.paypalCompletionUncertain) throw new Error('Checkout is unavailable');
      approval = { ...await createPaypalCheckout(current.formData, current.formDetails), fundingSource };
      if (disposed) throw new Error('Checkout changed');
      approvalHandled = false;
      return approval.id;
    };
    void loadPaypalSdk(clientId, kind).then(async sdk => {
      if (disposed) return;
      const options: Omit<PaypalButtonOptions, 'fundingSource'> = {
        style: { layout: 'vertical', label: 'paypal', shape: 'rect' },
        ...(kind === 'order' ? { createOrder: create } : { createSubscription: create }),
        onClick: (data, actions) => {
          if (disposed || failed || !ready || busy.current || latest.current.isSubmitting || latest.current.formData.paypalCompletionUncertain) return actions.reject();
          fundingSource = data.fundingSource ?? 'paypal';
          busy.current = true;
          setError(null);
          dispatch({ type: 'SET_SUBMISSION_ERROR', payload: null });
          dispatch({ type: 'SET_SUBMITTING', payload: true });
          return actions.resolve();
        },
        onApprove: async (data) => {
          if (disposed || failed || approvalHandled) return;
          approvalHandled = true;
          try {
            const current = latest.current;
            approvalStage = 'upgrade-context';
            if (current.mode === 'monthly-upgrade' && (!current.activeSubscription || !current.upgradeJwt)) throw new Error('Missing donation update details');
            const id = kind === 'order' ? data.orderID : data.subscriptionID;
            if (disposed) return;
            approvalStage = 'approval-match';
            if (!approval || id !== approval.id || !current.formDetails
              || approval.terms !== paypalTerms(current.formData, current.formDetails)) throw new Error('Checkout changed');
            approvalStage = 'approval-details';
            const donor = await loadPaypalApprovalDonor(approval);
            if (disposed) return;
            approvalStage = 'member-match';
            if (latest.current.memberId !== current.memberId) throw new Error('Member changed during approval');
            approvalStage = 'approval-routing';
            dispatch({ type: 'SET_PAYPAL_APPROVAL', payload: { approval, donor } });
            // Donations and Update Donation always confirm donor information after approval;
            // only payment-only Update Billing completes here.
            if (current.mode !== 'billing-update') {
              release();
              dispatch({ type: 'NEXT_STEP' });
              return;
            }
            const formData = { ...prefillPaypalDonor(current.formData, donor, true), paypalApproval: approval };
            approvalStage = 'completion';
            const result = await submitBillingUpdateFromState(current, formData);
            if (disposed) return;
            if (result.success && result.receipt) {
              dispatch({ type: 'SET_RECEIPT', payload: result.receipt });
            } else {
              const submissionError = getPrimarySubmissionError(result.errors, SUBMISSION_ERROR_FALLBACK_MESSAGE);
              dispatch({ type: 'SET_SUBMISSION_ERROR', payload: submissionError });
              if (submissionError.code === 'PAYPAL_COMPLETION_UNCERTAIN') dispatch({ type: 'SET_PAYPAL_UNCERTAIN' });
              else dispatch({ type: 'CLEAR_PAYPAL_APPROVAL' });
            }
            release();
          } catch {
            fail(approvalStage);
          }
        },
        onCancel: () => { if (!disposed && !failed) release(); },
        onError: error => {
          logger.warn('PayPal SDK checkout failed', {
            kind, ...paypalErrorDiagnostic(error),
            online: navigator.onLine, visibility: document.visibilityState,
          });
          fail('sdk-error');
        },
      };
      renderTimer = window.setTimeout(() => fail('render-timeout'), 20_000);
      // This launch offers PayPal only, even when Venmo is eligible.
      const button = sdk.Buttons({ ...options, fundingSource: 'paypal' });
      if (!button.isEligible()) throw new Error('No eligible PayPal options');
      buttons.push(button);
      const target = document.createElement('div');
      element.append(target);
      await button.render(target);
      window.clearTimeout(renderTimer);
      if (disposed || failed) return;
      ready = true;
      element.inert = false;
      setLoading(false);
    }).catch(() => fail('sdk-load-or-render'));
    return () => {
      disposed = true;
      window.clearTimeout(renderTimer);
      closeButtons();
    };
  }, [clientId, kind, terms, attempt, dispatch]);

  return <VStack align="stretch" gap={3}>
    <DonationTotalBar amount={state.formData.totalDonationAmount ?? state.formData.customAmount ?? state.formData.amount ?? 0} frequency={state.formData.frequency} />
    <Box
      position="relative"
      overflow="hidden"
      h={loading ? 12 : undefined}
      style={loading ? undefined : { height: error ? 0 : buttonsHeight }}
      transition="height 300ms ease"
      _motionReduce={{ transition: 'none' }}
      aria-busy={loading}
    >
      {loading && <Box
        role="status"
        aria-label="Loading PayPal payment options"
        position="absolute"
        inset={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Spinner size="sm" color="teal.solid" />
      </Box>}
      {/* The SDK needs layout to render; measure it outside the panel's flow. */}
      <Box
        ref={container}
        aria-label="PayPal payment options"
        aria-hidden={loading || Boolean(error)}
        position="absolute"
        top={0}
        insetInline={0}
        visibility={loading || error ? 'hidden' : 'visible'}
        opacity={loading || error ? 0 : 1}
        transition={loading || error ? 'none' : 'opacity 200ms ease 300ms'}
        _motionReduce={{ transition: 'none' }}
      />
    </Box>
    {error && <>
      <Text role="alert" fontSize="sm" color="red.fg">{error}</Text>
      <Button size="sm" variant="outline" onClick={() => setAttempt(value => value + 1)}>Try PayPal again</Button>
    </>}
    <Text fontSize="sm" color="fg.muted">
      {state.mode === 'billing-update' ? 'Approve with PayPal to update your billing.' : 'You’ll confirm your information after you approve payment.'}
    </Text>
  </VStack>;
}
